import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, Copy, Share2, Download } from "lucide-react";

const SHARE_DOMAIN = "ai-storycraft-studio.lovable.app";

export default function WaitlistConfirmed() {
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get("ref") || "";
  const [position, setPosition] = useState<number | null>(null);
  const [referralCount, setReferralCount] = useState(0);
  const [copied, setCopied] = useState(false);

  const shareUrl = `https://${SHARE_DOMAIN}/waitlist?ref=${refCode}`;
  const progressPercentage = (referralCount / 3) * 100;

  useEffect(() => {
    if (!refCode) return;

    const fetchData = async () => {
      // Get position
      const { data: entry } = await supabase
        .from("waitlist")
        .select("created_at, referral_count")
        .eq("referral_code", refCode)
        .single();

      if (entry) {
        setReferralCount(entry.referral_count);
        const { count } = await supabase
          .from("waitlist")
          .select("id", { count: "exact", head: true })
          .lte("created_at", entry.created_at);
        if (count !== null) setPosition(count);
      }
    };
    fetchData();
  }, [refCode]);

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success("Link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent("I just joined the AI StoryCraft Studio waitlist! Join me:")}&url=${encodeURIComponent(shareUrl)}`;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`I just joined the AI StoryCraft Studio waitlist! Join me: ${shareUrl}`)}`;

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4"
      style={{
        background: "#0D0D1A",
        backgroundImage: "radial-gradient(ellipse at 50% 50%, rgba(139, 92, 246, 0.08) 0%, transparent 60%)"
      }}
    >
      <Helmet>
        <title>You're on the Waitlist | AI StoryCraft Studio</title>
        <meta
          name="description"
          content="Your waitlist signup is confirmed. Share your referral link to unlock early access to AI StoryCraft Studio."
        />
        <link rel="canonical" href="https://ai-storycraft-studio.com/waitlist/confirmed" />
        <meta property="og:title" content="You're on the Waitlist | AI StoryCraft Studio" />
        <meta
          property="og:description"
          content="Your waitlist signup is confirmed. Share your referral link to unlock early access to AI StoryCraft Studio."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://ai-storycraft-studio.com/waitlist/confirmed" />
        <meta property="og:image" content="https://ai-storycraft-studio.com/mockups/mockup-nexis-ai.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="You're on the Waitlist | AI StoryCraft Studio" />
        <meta
          name="twitter:description"
          content="Your waitlist signup is confirmed. Share your referral link to unlock early access to AI StoryCraft Studio."
        />
        <meta name="twitter:image" content="https://ai-storycraft-studio.com/mockups/mockup-nexis-ai.png" />
      </Helmet>
      <div className="max-w-[520px] w-full text-center space-y-8" style={{ paddingTop: "15vh" }}>
        {/* Glowing Checkmark */}
        <div className="flex justify-center">
          <div
            style={{
              filter: "drop-shadow(0 0 24px rgba(139, 92, 246, 0.3))"
            }}
          >
            <CheckCircle2 className="w-16 h-16" color="#8B5CF6" strokeWidth={1.5} />
          </div>
        </div>

        {/* Main Heading */}
        <h1 className="text-4xl font-bold text-white">You're on the list! 🎉</h1>

        {/* Checklist Download */}
        <div className="space-y-3">
          <p className="text-lg font-semibold text-white">Your free checklist is waiting 👇</p>
          <a
            href="/creator_monetisation_checklist.pdf"
            download
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 font-bold text-white transition-opacity hover:opacity-90"
            style={{
              backgroundColor: "#8B5CF6",
              borderRadius: "10px",
              padding: "14px 28px",
            }}
          >
            <Download className="w-4 h-4" />
            Download The Creator Monetisation Checklist →
          </a>
        </div>

        {/* Position */}
        {position !== null && (
          <p className="text-lg" style={{ color: "rgba(255,255,255,0.6)" }}>
            Your position: <span className="font-bold" style={{ color: "#8B5CF6" }}>#{position}</span>
          </p>
        )}

        {/* Subtitle */}
        <p style={{ color: "rgba(255,255,255,0.6)" }}>
          Want early access? Share your link with 3 friends.
        </p>

        {/* Referral Link Box */}
        <div
          className="rounded-xl p-4 text-sm font-mono break-all flex items-center justify-between gap-3"
          style={{
            background: "rgba(255, 255, 255, 0.05)",
            border: "1px solid rgba(139, 92, 246, 0.3)",
            color: "rgba(255,255,255,0.8)"
          }}
        >
          <span className="flex-1 text-left">{shareUrl}</span>
          <button
            onClick={copyLink}
            className="px-3 py-1 rounded-full font-semibold text-white text-xs whitespace-nowrap transition-all duration-200 hover:opacity-90"
            style={{
              background: "#8B5CF6"
            }}
          >
            {copied ? "Copied!" : "Copy"}
          </button>
        </div>

        {/* Share Buttons */}
        <div className="flex gap-3 justify-center">
          <Button asChild variant="outline" size="sm" className="gap-2">
            <a href={twitterUrl} target="_blank" rel="noopener noreferrer">
              <Share2 className="w-4 h-4" /> Twitter
            </a>
          </Button>
          <Button asChild variant="outline" size="sm" className="gap-2">
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
              <Share2 className="w-4 h-4" /> WhatsApp
            </a>
          </Button>
        </div>

        {/* Referral Progress */}
        <div className="space-y-2 pt-4">
          <p className="text-xs" style={{ color: "rgba(255,255,255,0.5)" }}>
            Referral Progress
          </p>
          <div
            className="h-1.5 rounded-full overflow-hidden"
            style={{
              background: "rgba(255,255,255,0.1)"
            }}
          >
            <div
              className="h-full transition-all duration-300 rounded-full"
              style={{
                background: "#8B5CF6",
                width: `${Math.min(progressPercentage, 100)}%`
              }}
            />
          </div>
        </div>

        {/* Friends Referred Count */}
        <div>
          <p style={{ color: "rgba(255,255,255,0.5)" }}>
            Friends referred so far: <span className="font-bold" style={{ color: "#8B5CF6" }}>{referralCount}</span> / 3
          </p>
          {referralCount >= 3 && (
            <p className="text-sm font-semibold mt-3" style={{ color: "#8B5CF6" }}>
              🎉 You've unlocked early access!
            </p>
          )}
        </div>

        {/* Back Link */}
        <Link 
          to="/waitlist" 
          className="text-sm inline-block transition-colors"
          style={{ color: "#8B5CF6" }}
          onMouseEnter={(e) => e.currentTarget.style.textDecoration = "underline"}
          onMouseLeave={(e) => e.currentTarget.style.textDecoration = "none"}
        >
          Back to waitlist
        </Link>
      </div>
    </div>
  );
}
